import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CodeNotificationLevelProjectModal from "../components/modals/common/CodeNotificationLevelProjectModal";
import CodeNotificationSubmissionsModal from "../components/modals/common/CodeNotificationSubmissionsModal";
import { useNotification } from "../context/NotificationContext";
import { useHandleNotificationClick } from "../hooks/useHandleNotificationClick";
import authServices from "../services/authServices";
import { getLanguage } from "../utils/getLanguage";

const NotificationCenter = () => {
  const { notifications: contextNotifications, setNotifications } = useNotification();
  const lang = getLanguage();
  const { t } = useTranslation();

  // Local notifications state for pagination & infinite scroll
  const [notifications, setLocalNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  const { handleClick, studentCodeSolutionsModalRef, studentLevelProjectModalRef } =
    useHandleNotificationClick();

  // Sync context notifications into local notifications state (prepend new ones)
  useEffect(() => {
    setLocalNotifications((local) => {
      const localIds = new Set(local.map((n) => n.notification_id || n.id));
      const newOnes = contextNotifications.filter((n) => !localIds.has(n.notification_id || n.id));
      if (newOnes.length === 0) return local;
      return [...newOnes, ...local]; // prepend new notifications at the top
    });
  }, [contextNotifications]);

  // Wrap the hook to update local notifications as well on click
  const handleNotificationClick = async (notification) => {
    await handleClick(notification);

    if (!notification.read_at) {
      const updatedNotification = { ...notification, read_at: new Date().toISOString() };
      setLocalNotifications((prev) =>
        prev.map((n) => (n.id === updatedNotification.id ? updatedNotification : n))
      );
    }
  };

  const loadMoreNotifications = async (reset = false) => {
    if (loading || (!hasMore && !reset)) return;

    setLoading(true);
    try {
      // If resetting, always load page 1
      const currentPage = reset ? 1 : page;
      const response = await authServices.getNotifications({ page: currentPage, per_page: 10 });

      const newNotifications = response.data;
      const total = response.total;
      const perPage = response.per_page;

      if (reset) {
        setLocalNotifications(newNotifications);
        setNotifications(newNotifications);
        setPage(2); // next page after reset is 2
      } else {
        setLocalNotifications((prev) => [...prev, ...newNotifications]);
        setNotifications((prev) => [...prev, ...newNotifications]);
        setPage((prev) => prev + 1);
      }

      const totalPages = Math.ceil(total / perPage);
      if (currentPage >= totalPages) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMoreNotifications();
  }, []);

  // Reset and reload notifications when language changes
  useEffect(() => {
    setPage(1);
    setHasMore(true);
    setLocalNotifications([]);
    loadMoreNotifications(true); // pass true to reset state inside the loader
  }, [lang]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop === clientHeight) {
      loadMoreNotifications();
    }
  };

  return (
    <div className="mx-auto w-full max-w-3xl rounded-xl bg-white shadow-lg dark:bg-gray-900">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h2 className="text-lg font-bold dark:text-white">{t("notifications")}</h2>
      </div>
      <div className="max-h-[500px] overflow-y-auto" onScroll={handleScroll}>
        {notifications?.length > 0 ? (
          notifications.map((n, i) => (
            <NotificationItem
              key={i}
              notification={n}
              onClick={() => handleNotificationClick(n)}
              lang={lang}
            />
          ))
        ) : loading ? (
          <div className="flex items-center justify-center p-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          </div>
        ) : (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">No notifications</div>
        )}
        {loading && (
          <div className="flex items-center justify-center p-4">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
          </div>
        )}
      </div>
      <CodeNotificationSubmissionsModal ref={studentCodeSolutionsModalRef} />
      <CodeNotificationLevelProjectModal ref={studentLevelProjectModalRef} />
    </div>
  );
};

const NotificationItem = ({ notification, onClick, lang }) => {
  const { message, read_at } = notification;
  const messageText = typeof message === "string" ? message : lang === "ar" ? message?.ar : message?.en || "";

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      className={`flex cursor-pointer items-center gap-3 p-3 transition-colors ${
        !read_at ? "bg-blue-50 dark:bg-gray-800" : ""
      } hover:bg-gray-100 dark:hover:bg-gray-800`}
    >
      <div className="h-10 w-10 rounded-full bg-gray-300 text-center text-sm leading-10 dark:bg-gray-700">
        🔔
      </div>
      <div className="flex-1 text-xs text-gray-600 dark:text-gray-300">{messageText}</div>
      {!read_at && <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" />}
    </div>
  );
};

export default NotificationCenter;
